/**
 * Un rilascio nuovo toglie dall'origine gli asset del rilascio precedente (stg-tma serve
 * SOLO il rilascio corrente — v. CLAUDE.md). Chi ha la mini app aperta da prima e naviga
 * verso una schermata lazy prende un import fallito: Chrome dice «Failed to fetch
 * dynamically imported module», Safari «Importing a module script failed» (o, sul MIME
 * sbagliato di un 200 servito come HTML, «is not a valid JavaScript MIME type for module
 * script»), Firefox «error loading dynamically imported module». Un ricarico risolve — un
 * ricarico IN CICLO no, quindi la guardia e' la parte che conta, non il riconoscimento.
 */

const CHUNK_ERROR_PATTERNS: RegExp[] = [
  /importing a module script failed/i,
  /failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  // WebKit sul MIME sbagliato — misurato dalla revisione indipendente di #476: uno stesso
  // percorso servito 200 text/html invece di 404 (tma.qlash.gg oggi, latente: deploy:prod
  // non cancella gli asset vecchi) da' QUESTO messaggio, non "Importing a module script
  // failed", ed era invisibile prima.
  /is not a valid javascript mime type for module script/i,
];

function messageOf(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  return '';
}

export function isChunkLoadError(error: unknown): boolean {
  const message = messageOf(error);
  if (!message) return false;
  return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(message));
}

const STORAGE_KEY = 'qlash.chunk-reload-at';
/**
 * 🔴 CORRETTO dopo la revisione indipendente di #476: la prima stesura confrontava
 * l'ultimo ricarico con l'ISTANTE DEL FALLIMENTO (`Date.now()` al momento dell'errore), che
 * e' una finestra di FREQUENZA — non un limite sul NUMERO. Misurato sul banco: un
 * fallimento persistente che arriva oltre questa finestra dal ricarico precedente
 * ricaricava di nuovo (Chromium: 3 ricarichi in 42 s, ancora in corso). Ora si confronta
 * con `performance.timeOrigin` — l'istante in cui QUESTA pagina e' nata, fisso per tutta
 * la sua vita — quindi la domanda non e' piu' «e' passato abbastanza tempo dal ricarico?»
 * ma «questa pagina e' quella nata da quel ricarico?»: se si', non si ricarica MAI PIU' per
 * tutta la vita di questa pagina, non importa quanto tardi arrivi il secondo fallimento.
 * Un ricarico per EPISODIO, non per finestra.
 */
export const CHUNK_RELOAD_COOLDOWN_MS = 10_000;

/**
 * Pura: decide se un ricarico automatico e' ammesso, dato l'istante dell'ultimo ricarico
 * (o `null` se non c'e' mai stato) e l'istante di nascita della pagina CORRENTE
 * (`performance.timeOrigin`, fisso per tutta la vita della pagina — mai l'istante del
 * fallimento). Isolata dal browser cosi' il confine (`>`, non `>=`) si prova senza montare
 * niente, e cosi' si prova che un valore corrotto (`NaN`, un orologio andato indietro)
 * blocca invece di dividere per un caso che non si sa leggere.
 */
export function canAutoReload(
  lastReloadAt: number | null,
  pageOrigin: number,
  cooldownMs: number = CHUNK_RELOAD_COOLDOWN_MS,
): boolean {
  if (lastReloadAt === null) return true;
  return pageOrigin - lastReloadAt > cooldownMs;
}

interface AttemptDeps {
  /** Istante di nascita della pagina corrente — v. `PAGE_BORN_AT` qui sotto. */
  pageOrigin: () => number;
  /** Istante presente, usato SOLO per scrivere la guardia — mai per leggerla. */
  now: () => number;
  reload: () => void;
}

/**
 * 🔴 CORRETTO dopo la 2ª revisione indipendente di #476: la stesura precedente leggeva
 * `performance.timeOrigin` fresco a ogni chiamata mentre la guardia SCRIVE con `Date.now()`
 * — due orologi. Il motore deriva `timeOrigin` per conto proprio, e se si sfasa dal wall
 * clock usato per scrivere (misurato: telefono che corregge l'ora mentre la WebView vive) la
 * sottrazione nella guardia resta sempre oltre il cooldown: 815 ricarichi in 12 s sul banco.
 * ⇒ la nascita della pagina si prende UNA VOLTA, al caricamento di questo modulo (cioe' al
 * boot), con lo STESSO orologio (`Date.now() - performance.now()`) usato per scrivere:
 * qualunque cosa succeda ai due orologi dopo, la guardia confronta valori nati dalla stessa
 * base. Senza sfasamento la differenza con `performance.timeOrigin` e' 0-1 ms (misurato su
 * Chromium e WebKit) — qui non serve nemmeno quella precisione, basta un orologio solo.
 */
const PAGE_BORN_AT = Date.now() - performance.now();

const defaultDeps: AttemptDeps = {
  pageOrigin: () => PAGE_BORN_AT,
  now: () => Date.now(),
  reload: () => window.location.reload(),
};

/**
 * Tenta UN ricarico automatico per un errore di chunk vecchio. Ritorna `true` se ha
 * ricaricato, `false` altrimenti (non e' un errore di chunk, o la guardia ha bloccato).
 *
 * ⚠️ FAIL-CLOSED sul `sessionStorage`: se leggerlo o scriverlo lancia (storage non
 * disponibile, quota, modalita' privata di alcuni browser), la guardia non si puo'
 * dimostrare e si preferisce NON ricaricare — un ricarico senza guardia dimostrabile e'
 * esattamente il ciclo che il mandato vieta, mai un compromesso accettabile. Lo stesso vale
 * per un valore illeggibile in `sessionStorage` (`NaN`): `canAutoReload` lo confronta con
 * una sottrazione che non e' mai `> cooldownMs`, quindi blocca.
 */
export function attemptChunkReload(error: unknown, deps: AttemptDeps = defaultDeps): boolean {
  if (!isChunkLoadError(error)) return false;

  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    const lastReloadAt = raw === null ? null : Number(raw);
    if (!canAutoReload(lastReloadAt, deps.pageOrigin())) return false;

    window.sessionStorage.setItem(STORAGE_KEY, String(deps.now()));
    deps.reload();
    return true;
  } catch {
    return false;
  }
}

/**
 * Vite (>=4.4) spedisce questo evento su `window` quando un `import()` dinamico (o il
 * suo preload) fallisce — e' il segnale piu' precoce, prima ancora che React arrivi a
 * disfare l'albero. Registrato una volta, al boot; l'annullamento serve solo ai test.
 *
 * ⛔ NON si chiama `event.preventDefault()`: la revisione indipendente di #476 ha misurato
 * che farlo impedisce a Vite di far RIGETTARE la promise dell'import — la pagina generica
 * compariva per ~3 s durante il ricarico stesso, perche' React si trovava un modulo
 * `undefined` invece dell'errore di chunk atteso. Lasciando il rifiuto intatto, lo stesso
 * errore arriva anche al router (secondo gancio) con la frase giusta, e basta.
 */
export function registerChunkPreloadErrorHandler(target: Window = window): () => void {
  const handler = (event: Event) => {
    const payload = (event as Event & { payload?: unknown }).payload;
    attemptChunkReload(payload);
  };
  target.addEventListener('vite:preloadError', handler);
  return () => target.removeEventListener('vite:preloadError', handler);
}
